import useSWR from 'swr'
import {
  DatumTags,
  DatumType,
  Personality,
  PersonalityDatum,
} from '@crystal-eyes/types'
import useAuth from '@crystal-eyes/hooks/useAuth'
import { useMemo } from 'react'
import {
  queryWithAuth,
  queryWithoutAuth,
  gql,
} from '@crystal-eyes/utils/apis/graphqlApi'

type Options = {
  personality?: Personality
  tags: DatumTags[]
  type: DatumType
  limit: number
  fallbackKey?: string
}

export type State = {
  data?: PersonalityDatum[]
  loading?: boolean
  error?: any
}

export default function useDatums({
  personality,
  tags: phraseTags,
  limit,
  type,
  fallbackKey,
}: Options): State {
  const { data: auth } = useAuth()

  const fetcher = ([_key, _authedUser, variables]: [
    string,
    string,
    object,
  ]) => {
    if (auth?.authed) {
      return queryWithAuth(auth, DATUMS_QUERY, variables)
    }
    return queryWithoutAuth(PUBLIC_DATUMS_QUERY, variables)
  }

  const tags = useMemo(() => [...phraseTags, type], [phraseTags, type])
  const query = personality
    ? [
        `useDatums_${auth?.authed ? 'Auth' : 'Public'}`,
        auth?.jwtPayload?.sub,
        { personalityId: personality.id, limit, tags },
      ]
    : null

  const fallbackData =
    fallbackKey && personality && (personality as any)[fallbackKey]

  const {
    data: queryData,
    isLoading: loading,
    error: error,
  } = useSWR(query, fetcher, {
    revalidateOnFocus: false,
    keepPreviousData: true,
    fallbackData:
      personality && fallbackData
        ? { personality: { ...personality, datums: fallbackData } }
        : undefined,
  })

  return {
    data:
      queryData?.personality?.datums || queryData?.public?.personality.datums,
    loading,
    error,
  }
}

const DATUMS_QUERY = gql`
  query UseDatums(
    $personalityId: Guid!
    $tags: [PersonalityDatumTag!]!
    $limit: Int!
  ) {
    personality(id: $personalityId) {
      datums(tags: $tags, limit: $limit) {
        id
        text

        endorsements {
          id

          personalInfo {
            firstName
            lastName
            fullName
            photoUrl
          }

          snapshot {
            personality {
              id
              discType
            }
          }
        }

        premiumBlur
      }
    }
  }
`

const PUBLIC_DATUMS_QUERY = gql`
  query UseDatums(
    $personalityId: Guid!
    $tags: [PersonalityDatumTag!]!
    $limit: Int!
  ) {
    public {
      personality(id: $personalityId) {
        datums(tags: $tags, limit: $limit) {
          id
          text
          premiumBlur
        }
      }
    }
  }
`
